// @djsem/live-core — Live Show Conductor: simulator-only foundation (Prompt 12E).
//
// Snapshot-driven live conductor CORE for the simulator/replay path. Consumes normalized
// live events + commands, keeps playback state, resolves active chapter/cue, and always
// produces a defined DJSEMOutputFrame. Loads APPROVED snapshots only (rules 4, 11); safe
// mode / blackout override all automation (rule 9). No Beat Link, no Electron/Java bridge,
// no hardware output — those are later prompts.
//
// Naming note: the prompt calls the state `DJSEMLiveConductorState`, but that name is already
// taken by the older beat-link conductor in types.ts. To avoid an ambiguous re-export we
// expose this session-oriented state as `DJSEMLiveConductorSimState`.

import type { DJSEMLiveEventEnvelope } from "./protocol";
import { SeqCounter } from "@djsem/shared";
import type { LivePlaybackState } from "./types";
import type { DJSEMOutputFrame } from "@djsem/render-state";
import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/**
 * Session-oriented state for the simulator/replay-path live conductor (distinct from the
 * older beat-link `DJSEMLiveConductorState` in types.ts, hence the `Sim` suffix).
 */
export interface DJSEMLiveConductorSimState {
  sessionId: string;
  status: "idle" | "running" | "paused" | "safe" | "blackout" | "degraded" | "failed";
  activeSnapshotId?: string;
  activeTrackId?: string;
  activeChapterId?: string;
  activeCueId?: string;
  playbackState: LivePlaybackState;
  outputFrame: DJSEMOutputFrame;
  warnings: string[];
  // 23R: operator intensity control (0..1 master scaler; safety still wins) + performance log.
  masterIntensity?: number;
  performanceLog?: Array<{ at: string; kind: string; detail?: string }>;
  updatedAt: string;
}

/** An operator command applied to a {@link DJSEMLiveConductorSimState} via {@link LiveShowConductorCore.applyCommand}. */
export interface DJSEMLiveConductorCommand {
  commandId: string;
  commandType:
    | "load_snapshot" | "start" | "pause" | "resume" | "select_track" | "manual_match"
    | "trigger_manual_cue" | "set_intensity" | "enable_safe_mode" | "disable_safe_mode" | "blackout" | "clear_blackout" | "stop";
  payload?: unknown;
  createdAt: string;
}

interface ApprovedPackageSnapshotLike { snapshotId: string; snapshotKind: "approved_package_snapshot"; snapshotHash: string; preparationTrackId: string; finalPackage: Record<string, unknown>; }
interface ApprovedSetSnapshotLike {
  snapshotId: string; snapshotKind: "approved_set_snapshot"; snapshotHash: string;
  finalSet: { id: string; tracks: Array<{ preparationTrackId: string }>; chapters: Array<{ id: string; startTimeMs?: number; endTimeMs?: number }> };
}
type ApprovedSnapshotLike = ApprovedPackageSnapshotLike | ApprovedSetSnapshotLike;

/** Input for {@link LiveShowConductorCore.createSession}. */
export interface CreateLiveConductorSessionInput { sessionId?: string; createdAt?: string; }
/** Input for {@link LiveShowConductorCore.loadApprovedSnapshot}. */
export interface LoadLiveSnapshotInput { state: DJSEMLiveConductorSimState; snapshot: unknown; }
/** Input for {@link LiveShowConductorCore.applyLiveEvent}. */
export interface ApplyLiveEventInput { state: DJSEMLiveConductorSimState; event: DJSEMLiveEventEnvelope; snapshot?: ApprovedSnapshotLike; }
/** Input for {@link LiveShowConductorCore.applyCommand}. */
export interface ApplyLiveConductorCommandInput { state: DJSEMLiveConductorSimState; command: DJSEMLiveConductorCommand; snapshot?: ApprovedSnapshotLike; }
/** Input for {@link LiveShowConductorCore.produceOutputFrame}. */
export interface ProduceOutputFrameInput { state: DJSEMLiveConductorSimState; }

const AT = "1970-01-01T00:00:00.000Z";

function safeFrame(now: number, reason: string, pkgId?: string): DJSEMOutputFrame {
  return { timestamp: now, packageId: pkgId,
    ledWall: { mode: "safe_hold", palette: [], intensity: 0, motion: "none", safe: true },
    lasers: { mode: "safe_hold", intensity: 0, triggers: [], safe: true },
    projectors: { mode: "safe_hold", emojiSequence: [], density: 0, intensity: 0, safe: true },
    intensityBudget: { total: 0, ledWall: 0, lasers: 0, projectors: 0, dominantLayer: "safe" },
    safety: { safeModeActive: true, fallbackVisualMode: "safe_hold", reason } };
}
function blackoutFrame(now: number, pkgId?: string): DJSEMOutputFrame {
  return { timestamp: now, packageId: pkgId,
    ledWall: { mode: "blackout", palette: [], intensity: 0, motion: "none", safe: true },
    lasers: { mode: "blackout", intensity: 0, triggers: [], safe: true },
    projectors: { mode: "blackout", emojiSequence: [], density: 0, intensity: 0, safe: true },
    intensityBudget: { total: 0, ledWall: 0, lasers: 0, projectors: 0, dominantLayer: "blackout" },
    safety: { safeModeActive: true, fallbackVisualMode: "blackout", reason: "blackout" } };
}
function activeFrame(now: number, pkgId: string | undefined, cueId: string | undefined, intensity: number): DJSEMOutputFrame {
  const cl = Math.max(0, Math.min(1, intensity));
  return { timestamp: now, packageId: pkgId, cueId,
    ledWall: { mode: "active", palette: [], intensity: cl, motion: "medium", safe: false },
    lasers: { mode: "active", intensity: cl * 0.6, triggers: [], safe: false },
    projectors: { mode: "active", emojiSequence: [], density: cl * 0.8, intensity: cl * 0.8, safe: false },
    intensityBudget: { total: cl, ledWall: cl, lasers: cl * 0.6, projectors: cl * 0.8, dominantLayer: "ledWall" },
    safety: { safeModeActive: false, fallbackVisualMode: undefined, reason: undefined } };
}

function isApproved(s: unknown): s is ApprovedSnapshotLike {
  const k = (s as { snapshotKind?: string })?.snapshotKind;
  return (k === "approved_package_snapshot" || k === "approved_set_snapshot") && !!(s as { snapshotHash?: string }).snapshotHash;
}
function chaptersFrom(s?: ApprovedSnapshotLike): Array<{ id: string; startTimeMs?: number; endTimeMs?: number }> {
  return s && s.snapshotKind === "approved_set_snapshot" ? s.finalSet.chapters : [];
}

// ---- service (class-based; hard rule 12) ------------------------------------

const _seq = new SeqCounter();
/**
 * Snapshot-driven live conductor core for the simulator/replay path: consumes normalized
 * live events and operator commands, tracks playback/chapter/cue state, and always
 * produces a defined output frame. Only APPROVED snapshots may be loaded; safe mode and
 * blackout override all automation. No Beat Link, no Electron/Java bridge, no hardware
 * output.
 */
export class LiveShowConductorCore {
  /**
   * Creates a fresh idle session.
   * @param input - optional session id and creation timestamp.
   * @returns a new idle conductor sim state with a safe-hold output frame.
   */
  createSession(input: CreateLiveConductorSessionInput): DJSEMLiveConductorSimState {
    const at = input.createdAt ?? AT;
    return {
      sessionId: input.sessionId ?? `lc_${_seq.next()}`, status: "idle",
      playbackState: { positionMs: 0, isPlaying: false } as unknown as LivePlaybackState,
      outputFrame: safeFrame(0, "idle"), warnings: [], updatedAt: at,
    };
  }

  /**
   * Loads an approved package/set snapshot into the session. Snapshots that are not an
   * approved kind are refused and the session is marked failed (never loaded).
   * @param input - the current state and the candidate snapshot.
   * @returns the updated state, either idle-with-snapshot-loaded or failed with a warning.
   */
  loadApprovedSnapshot(input: LoadLiveSnapshotInput): DJSEMLiveConductorSimState {
    if (!isApproved(input.snapshot)) {
      return { ...input.state, status: "failed", warnings: [...input.state.warnings, "Refused: only approved snapshots may be loaded for live."], outputFrame: safeFrame(0, "load refused") };
    }
    const snap = input.snapshot;
    const trackId = snap.snapshotKind === "approved_set_snapshot" ? snap.finalSet.tracks[0]?.preparationTrackId : snap.preparationTrackId;
    const pkgId = snap.snapshotKind === "approved_set_snapshot" ? snap.finalSet.id : snap.snapshotId;
    return { ...input.state, status: "idle", activeSnapshotId: snap.snapshotId, activeTrackId: trackId, outputFrame: safeFrame(0, "snapshot loaded", pkgId) };
  }

  /**
   * Applies a normalized live event to the session, updating playback position/chapter and
   * re-deriving the output frame. Events with no usable position degrade a running session
   * rather than throwing.
   * @param input - the current state, the event envelope, and an optional snapshot for chapter lookup.
   * @returns the updated conductor sim state.
   */
  applyLiveEvent(input: ApplyLiveEventInput): DJSEMLiveConductorSimState {
    const s = { ...input.state };
    const payload = (input.event as { payload?: { positionMs?: number; timeMs?: number } }).payload ?? {};
    const pos = Number(payload.positionMs ?? payload.timeMs);
    if (Number.isFinite(pos) && s.status === "running") {
      s.playbackState = { ...(s.playbackState as object), positionMs: Math.max(0, pos), isPlaying: true } as unknown as LivePlaybackState;
      const ch = chaptersFrom(input.snapshot).find((c) => typeof c.startTimeMs === "number" && typeof c.endTimeMs === "number" && pos >= c.startTimeMs! && pos < c.endTimeMs!);
      s.activeChapterId = ch?.id ?? s.activeChapterId;
    } else if (!Number.isFinite(pos)) {
      s.warnings = [...s.warnings, "Live event had no usable position; state degraded."];
      if (s.status === "running") s.status = "degraded";
    }
    s.outputFrame = this.produceOutputFrame({ state: s });
    s.updatedAt = String((input.event as { occurredAt?: string }).occurredAt ?? AT);
    return s;
  }

  /**
   * Applies an operator command (start/pause/resume/blackout/safe-mode/manual cue/etc.) to
   * the session and re-derives the output frame. Every command is appended to the
   * performance log.
   * @param input - the current state, the command, and an optional snapshot.
   * @returns the updated conductor sim state.
   */
  applyCommand(input: ApplyLiveConductorCommandInput): DJSEMLiveConductorSimState {
    const s = { ...input.state };
    switch (input.command.commandType) {
      case "load_snapshot": return this.loadApprovedSnapshot({ state: s, snapshot: (input.command.payload as { snapshot?: unknown })?.snapshot });
      case "start":
        // Regression guard: "start" used to unconditionally flip status to "running" with no
        // check that an approved snapshot was ever successfully loaded (activeSnapshotId set by
        // loadApprovedSnapshot). A caller could go create -> start with no load_snapshot at all,
        // or after a load_snapshot that was refused (status "failed"), and this class's own
        // header comment promises "Loads APPROVED snapshots only" — "running" with no snapshot
        // contradicts that. Refuse and stay put instead.
        if (!s.activeSnapshotId) {
          s.warnings = [...s.warnings, "Refused: cannot start — no approved snapshot has been loaded."];
          break;
        }
        s.status = "running"; s.playbackState = { ...(s.playbackState as object), isPlaying: true } as unknown as LivePlaybackState; break;
      case "pause": if (s.status === "running") s.status = "paused"; break;
      case "resume": if (s.status === "paused") s.status = "running"; break;
      case "stop": s.status = "idle"; s.playbackState = { ...(s.playbackState as object), isPlaying: false } as unknown as LivePlaybackState; break;
      case "select_track": s.activeTrackId = String((input.command.payload as { trackId?: string })?.trackId ?? s.activeTrackId); break;
      case "manual_match": s.activeTrackId = String((input.command.payload as { trackId?: string })?.trackId ?? s.activeTrackId); break;
      case "trigger_manual_cue": { const cueId = String((input.command.payload as { cueId?: string })?.cueId ?? "manual_cue"); s.activeCueId = cueId; s.warnings = [...s.warnings, `Manual cue triggered: ${cueId}`]; break; }
      case "set_intensity": { const v = Number((input.command.payload as { intensity?: number })?.intensity); s.masterIntensity = Math.max(0, Math.min(1, Number.isFinite(v) ? v : 1)); break; }
      case "enable_safe_mode": s.status = "safe"; break;
      case "disable_safe_mode": if (s.status === "safe") s.status = "running"; break;
      case "blackout": s.status = "blackout"; break;
      case "clear_blackout": if (s.status === "blackout") s.status = "running"; break;
    }
    s.performanceLog = [...(s.performanceLog ?? []), { at: input.command.createdAt, kind: input.command.commandType, detail: s.activeCueId }];
    s.outputFrame = this.produceOutputFrame({ state: s });
    return s;
  }

  /**
   * Derives the output frame for the session's current status (blackout/safe/running/idle).
   * @param input - the session state to derive a frame from.
   * @returns the resulting output frame — always defined, never undefined.
   */
  produceOutputFrame(input: ProduceOutputFrameInput): DJSEMOutputFrame {
    const s = input.state;
    const pos = Number((s.playbackState as { positionMs?: number })?.positionMs ?? 0);
    const pkgId = s.activeSnapshotId;
    if (s.status === "blackout") return blackoutFrame(pos, pkgId);
    if (s.status === "safe") return safeFrame(pos, "safe mode", pkgId);
    if (s.status === "running") return activeFrame(pos, pkgId, s.activeCueId, 0.6 * (typeof s.masterIntensity === "number" ? s.masterIntensity : 1));
    return safeFrame(pos, `idle (${s.status})`, pkgId);
  }

  /**
   * Validates structural invariants of a conductor sim state (session id present, output
   * frame always defined).
   * @param state - the state to validate.
   * @returns a validation result listing any structural issues.
   */
  validateState(state: DJSEMLiveConductorSimState): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!state?.sessionId) issues.push({ path: "$.sessionId", message: "required" });
    if (!state?.outputFrame) issues.push({ path: "$.outputFrame", message: "output frame must always be defined" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "live_conductor_sim");
  }
}
